import { startTransition, useCallback, useEffect, useState } from "react";

interface UseTopLevelViewArgs {
  setSelectedChannel: (id: string | null) => void;
}

/**
 * Owns the top-level view toggles (lists overview, coworkers, library, skills,
 * connected tools) + the `selectedListId` cursor + matching window-event
 * listeners for `ano:open-coworker` / `ano:open-integrations` /
 * `ano:team-open-skills` / `ano:team-open-tools` / skill editor deeplinks.
 *
 * Every transition between views funnels through `closeAllSideViews` so
 * the priority order in `useActiveView` is honored — when one becomes
 * truthy, the others go falsy in the same `startTransition`.
 */
export function useTopLevelView({ setSelectedChannel }: UseTopLevelViewArgs) {
  const [showLists, setShowLists] = useState(false);
  const [showCoworkers, setShowCoworkers] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showAutomations, setShowAutomations] = useState(false);
  const [showSkills, setShowSkills] = useState(false);
  const [showConnectedTools, setShowConnectedTools] = useState(false);
  const [showInvites, setShowInvites] = useState(false);
  const [selectedListId, setSelectedListId] = useState<string | null>(null);

  const closeAllSideViews = useCallback(() => {
    setShowLists(false);
    setShowCoworkers(false);
    setShowLibrary(false);
    setShowAutomations(false);
    setShowSkills(false);
    setShowConnectedTools(false);
    setShowInvites(false);
    setSelectedListId(null);
  }, []);

  const openStandalonePage = useCallback(
    (page: "skills" | "connected-tools") => {
      startTransition(() => {
        setShowSkills(page === "skills");
        setShowConnectedTools(page === "connected-tools");
        setShowLists(false);
        setShowCoworkers(false);
        setShowAutomations(false);
        setShowLibrary(false);
        setShowInvites(false);
        setSelectedListId(null);
        setSelectedChannel(null);
      });
    },
    [setSelectedChannel],
  );

  const handleToggleAutomations = useCallback(() => {
    startTransition(() => {
      setShowAutomations((prev) => {
        const next = !prev;
        if (next) {
          setShowLists(false);
          setShowCoworkers(false);
          setShowLibrary(false);
          setShowSkills(false);
          setShowConnectedTools(false);
          setShowInvites(false);
          setSelectedListId(null);
          setSelectedChannel(null);
        }
        return next;
      });
    });
  }, [setSelectedChannel]);

  const handleToggleLists = useCallback(() => {
    startTransition(() => {
      // If a specific list is open, go to the lists overview
      if (selectedListId) {
        setSelectedListId(null);
        setShowLists(true);
        return;
      }
      // Already on overview — stay
      if (showLists) return;
      // Open overview
      setShowLists(true);

      setShowCoworkers(false);
      setShowAutomations(false);
      setShowLibrary(false);
      setShowSkills(false);
      setShowConnectedTools(false);
      setShowInvites(false);
      setSelectedListId(null);
      setSelectedChannel(null);
    });
  }, [selectedListId, showLists, setSelectedChannel]);

  const handleToggleCoworkers = useCallback(() => {
    startTransition(() => {
      setShowCoworkers((prev) => {
        const next = !prev;
        if (next) {
          setShowLists(false);
          setShowAutomations(false);
          setShowLibrary(false);
          setShowSkills(false);
          setShowConnectedTools(false);
          setShowInvites(false);
          setSelectedListId(null);
          setSelectedChannel(null);
        }
        return next;
      });
    });
  }, [setSelectedChannel]);

  const handleToggleInvites = useCallback(() => {
    startTransition(() => {
      setShowInvites((prev) => {
        const next = !prev;
        if (next) {
          setShowLists(false);
          setShowCoworkers(false);
          setShowAutomations(false);
          setShowLibrary(false);
          setShowSkills(false);
          setShowConnectedTools(false);
          setSelectedListId(null);
          setSelectedChannel(null);
        }
        return next;
      });
    });
  }, [setSelectedChannel]);

  const handleSelectList = useCallback(
    (id: string | null) => {
      setSelectedListId(id);
      if (id) {
        setShowLists(false);
        setShowAutomations(false);
        setShowLibrary(false);
        setShowSkills(false);
        setShowConnectedTools(false);
        setShowInvites(false);

        setSelectedChannel(null);
      }
    },
    [setSelectedChannel],
  );

  // `ano:add-coworker` is consumed by useTeamEventListenerBridge inside
  // TeamView; outside the team view it's a no-op.

  // Open specific coworker in canvas editor
  useEffect(() => {
    const handler = (e: Event) => {
      const { id } = (e as CustomEvent).detail as { id: string };
      startTransition(() => {
        setShowCoworkers(true);

        setShowLists(false);
        setShowAutomations(false);
        setShowLibrary(false);
        setShowSkills(false);
        setShowConnectedTools(false);
        setShowInvites(false);
        setSelectedListId(null);
        setSelectedChannel(null);
      });
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("ano:coworkers-select", { detail: { id } }),
        );
      }, 100);
    };
    window.addEventListener("ano:open-coworker", handler);
    return () => window.removeEventListener("ano:open-coworker", handler);
  }, [setSelectedChannel]);

  // Open the Integrations page (was a modal; UserProfileFooter dispatches
  // this when its "Integrations" menu item is clicked).
  useEffect(() => {
    const handler = () => {
      startTransition(() => {
        setShowLibrary(true);
        setShowCoworkers(false);
        setShowLists(false);
        setShowAutomations(false);
        setShowSkills(false);
        setShowConnectedTools(false);
        setShowInvites(false);
        setSelectedListId(null);
        setSelectedChannel(null);
      });
    };
    window.addEventListener("ano:open-integrations", handler);
    return () => window.removeEventListener("ano:open-integrations", handler);
  }, [setSelectedChannel]);

  // Open the Automations page.
  useEffect(() => {
    const handler = () => {
      startTransition(() => {
        setShowAutomations(true);
        setShowLibrary(false);
        setShowCoworkers(false);
        setShowLists(false);
        setShowSkills(false);
        setShowConnectedTools(false);
        setShowInvites(false);
        setSelectedListId(null);
        setSelectedChannel(null);
      });
    };
    window.addEventListener("ano:open-automations", handler);
    return () => window.removeEventListener("ano:open-automations", handler);
  }, [setSelectedChannel]);

  // Workspace settings cog → standalone Skills / Connected Tools pages.
  useEffect(() => {
    const skillsHandler = () => openStandalonePage("skills");
    const toolsHandler = () => openStandalonePage("connected-tools");
    window.addEventListener("ano:team-open-skills", skillsHandler);
    window.addEventListener("ano:team-open-tools", toolsHandler);
    return () => {
      window.removeEventListener("ano:team-open-skills", skillsHandler);
      window.removeEventListener("ano:team-open-tools", toolsHandler);
    };
  }, [openStandalonePage]);

  // Deep-link into the Skills editor from agent canvas (SkillsNode) or
  // other app surfaces — opens the standalone Skills page, then defers
  // the editor transition until SkillsPage mounts.
  useEffect(() => {
    const createHandler = () => {
      openStandalonePage("skills");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("ano:skills-create"));
      }, 100);
    };
    const editHandler = (e: Event) => {
      const { id } = (e as CustomEvent).detail as { id: string };
      openStandalonePage("skills");
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("ano:skills-edit", { detail: { id } }),
        );
      }, 100);
    };
    window.addEventListener("ano:create-skill", createHandler);
    window.addEventListener("ano:edit-skill", editHandler);
    return () => {
      window.removeEventListener("ano:create-skill", createHandler);
      window.removeEventListener("ano:edit-skill", editHandler);
    };
  }, [openStandalonePage]);

  return {
    showLists,
    setShowLists,
    showCoworkers,
    setShowCoworkers,
    showLibrary,
    setShowLibrary,
    showAutomations,
    setShowAutomations,
    showSkills,
    setShowSkills,
    showConnectedTools,
    setShowConnectedTools,
    showInvites,
    setShowInvites,
    selectedListId,
    setSelectedListId,
    closeAllSideViews,
    handleToggleLists,
    handleToggleCoworkers,
    handleToggleAutomations,
    handleToggleInvites,
    handleSelectList,
  };
}
